import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://metamorphobiz.com';
    const schemaId = 'schema-home';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify([
        {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'MetamorphoBiz Inc.',
          url: siteUrl,
          logo: `${siteUrl}/logo.png`,
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+1-844-667-7433',
            contactType: 'customer service',
            areaServed: 'US',
            availableLanguage: 'English',
          },
          address: {
            '@type': 'PostalAddress',
            streetAddress: '340 N Sam Houston Pkwy E Ste A165G',
            addressLocality: 'Houston',
            addressRegion: 'TX',
            postalCode: '77060',
            addressCountry: 'US',
          },
          email: 'contact@metamorphobiz.com',
          sameAs: [],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'MetamorphoBiz Inc.',
          description: 'MetamorphoBiz is a Houston, TX–based technology firm specializing in AI-driven decision intelligence for government organizations. We combine advanced analytics, machine learning, and data modernization to help agencies improve operational efficiency, detect risk, and make data-driven decisions with confidence.',
          url: siteUrl,
          telephone: '+1-844-667-7433',
          email: 'contact@metamorphobiz.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '340 N Sam Houston Pkwy E Ste A165G',
            addressLocality: 'Houston',
            addressRegion: 'TX',
            postalCode: '77060',
            addressCountry: 'US',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 29.9902,
            longitude: -95.3677,
          },
          priceRange: '$$',
        },
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'MetamorphoBiz',
          url: siteUrl,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${siteUrl}/?s={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'MetamorphoBiz Government AI & Decision Intelligence',
          description: 'MetamorphoBiz is a Houston, TX–based technology firm specializing in AI-driven decision intelligence for government organizations. We combine advanced analytics, machine learning, and data modernization to help agencies improve operational efficiency, detect risk, and make data-driven decisions with confidence.',
          url: siteUrl,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', url: siteUrl },
        },
      ]);
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      const sections = ['home', 'services', 'solutions', 'case-studies', 'about', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d6msqnaqoe30lj0v8fgg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/8cd6047605fa8bd9b83f93f5c3fc7ad9/47c456da8a70802ef8f75066d63b6e27.png" 
                alt="MetamorphoBiz Logo" 
                className="h-12 w-auto"
              />
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              {[
                { id: 'home', label: 'Home' },
                { id: 'services', label: 'Services' },
                { id: 'solutions', label: 'Solutions' },
                { id: 'case-studies', label: 'Case Studies' },
                { id: 'about', label: 'About Us' },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeSection === item.id
                      ? scrolled ? 'text-teal-600' : 'text-white'
                      : scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <Link
                to="/contact"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
                }`}
              >
                Contact Us
              </Link>
            </div>

            <button 
              onClick={() => scrollToSection('contact')}
              className="hidden md:block px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-all hover:shadow-lg cursor-pointer whitespace-nowrap"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/6fc818f23b7fae09ba57266405ce702a.jpg"
            alt="Data Analytics Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 via-slate-900/80 to-slate-900/90"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="space-y-8">
            <h1 className="text-6xl md:text-7xl font-bold text-white leading-tight">
              AI-Driven Decision Intelligence &amp; Data Modernization for<br />
              <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                Government Decision Systems
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Advanced analytics, AI, and operational optimization solutions that help government organizations make intelligent, data-driven decisions
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <button 
                onClick={() => scrollToSection('solutions')}
                className="px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-all hover:shadow-2xl hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                Explore Solutions
              </button>
              <button 
                onClick={() => scrollToSection('case-studies')}
                className="px-8 py-4 border-2 border-white text-white text-base font-semibold rounded-full hover:bg-white hover:text-slate-900 transition-all cursor-pointer whitespace-nowrap"
              >
                View Case Studies
              </button>
            </div>

            <div className="pt-8 flex items-center justify-center gap-3 text-slate-400">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-teal-400 text-xl"></i>
                <span className="text-sm font-medium">Trusted by 50+ Government Agencies</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-12 mb-16">
            <div className="w-1 h-16 bg-teal-600 rounded-full"></div>
            <div className="flex-1">
              <h2 className="text-5xl font-bold text-slate-900 mb-4">What We Do</h2>
              <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
                Comprehensive AI-driven solutions designed to transform how organizations leverage data for strategic decision-making, operational efficiency, and financial resilience.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-cpu-line',
                title: 'AI-Driven Operational Intelligence',
                description: 'Embed artificial intelligence into your core operations to automate complex workflows, surface hidden inefficiencies, and deliver real-time intelligence that empowers faster, smarter decisions across every department.',
                gradient: 'from-teal-500 to-cyan-500',
                dark: false
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Predictive Analytics and Forecasting',
                description: 'Leverage advanced machine learning models to anticipate trends, identify risks, and optimize resource allocation. Our predictive engine processes vast amounts of data to provide accurate, actionable predictions for strategic planning.',
                gradient: 'from-slate-700 to-slate-900',
                dark: true
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Fraud Detection and Financial Risk Modeling',
                description: 'Protect your organization with AI-powered fraud detection and financial risk models that identify anomalies, flag suspicious activity, and quantify exposure before it becomes a liability.',
                gradient: 'from-teal-500 to-cyan-500',
                dark: false
              },
              {
                icon: 'ri-database-2-line',
                title: 'Data Strategy and Architecture',
                description: 'Build a scalable, secure, and future-ready data foundation. We design end-to-end data architectures — from ingestion and storage to governance and access — aligned with your strategic objectives.',
                gradient: 'from-teal-500 to-cyan-500',
                dark: false
              },
              {
                icon: 'ri-bar-chart-box-line',
                title: 'Business Intelligence Dashboards',
                description: 'Transform raw data into clear, interactive dashboards that give decision-makers instant visibility into KPIs, operational metrics, and performance trends — all in one unified view.',
                gradient: 'from-slate-700 to-slate-900',
                dark: true
              },
              {
                icon: 'ri-settings-3-line',
                title: 'Workflow Automation',
                description: 'Eliminate manual bottlenecks and reduce operational overhead by automating repetitive processes. Our intelligent automation solutions integrate seamlessly with existing systems to deliver measurable efficiency gains.',
                gradient: 'from-teal-500 to-cyan-500',
                dark: false
              }
            ].map((service, index) => (
              <div
                key={index}
                className={`group relative rounded-3xl p-8 transition-all duration-300 hover:-translate-y-2 cursor-pointer ${
                  service.dark ? 'bg-slate-900' : 'bg-white shadow-lg hover:shadow-2xl'
                }`}
              >
                <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform`}>
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>
                
                <h3 className={`text-xl font-bold mb-4 ${service.dark ? 'text-white' : 'text-slate-900'}`}>
                  {service.title}
                </h3>
                
                <p className={`text-base leading-relaxed mb-6 ${service.dark ? 'text-slate-300' : 'text-slate-600'}`}>
                  {service.description}
                </p>
                
                <button className={`flex items-center gap-2 font-semibold text-sm group-hover:gap-3 transition-all cursor-pointer whitespace-nowrap ${
                  service.dark ? 'text-teal-400' : 'text-teal-600'
                }`}>
                  Learn More
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-slate-900 mb-4">Solutions</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tailored platforms designed to address the unique challenges faced by government organizations
            </p>
          </div>

          <div className="space-y-32">
            {[
              {
                title: 'Intelligent Data Governance Platform',
                tag: 'Data Management',
                description: 'Centralize, secure, and manage all your data assets with our comprehensive governance platform. Ensure compliance, maintain data quality, and enable seamless access across departments while maintaining the highest security standards.',
                features: [
                  'Automated data classification and cataloging',
                  'Role-based access control and audit trails',
                  'Real-time compliance monitoring',
                  'Integration with existing systems'
                ],
                image: 'https://readdy.ai/api/search-image?query=modern%20data%20governance%20dashboard%20interface%20showing%20organized%20data%20catalog%20with%20security%20controls%20and%20compliance%20metrics%2C%20clean%20professional%20UI%20with%20blue%20and%20teal%20accent%20colors%2C%20government-grade%20security%20visualization%2C%20structured%20data%20hierarchy%20display%2C%20contemporary%20corporate%20design%20aesthetic&width=800&height=600&seq=solution-001&orientation=landscape',
                reverse: false
              },
              {
                title: 'Predictive Analytics Engine',
                tag: 'AI & Machine Learning',
                description: 'Harness the power of machine learning to forecast trends, identify risks, and optimize resource allocation. Our predictive engine processes vast amounts of data to provide accurate, actionable predictions for strategic planning.',
                features: [
                  'Advanced forecasting algorithms',
                  'Risk assessment and mitigation tools',
                  'Resource optimization recommendations',
                  'Customizable prediction models'
                ],
                image: 'https://readdy.ai/api/search-image?query=sophisticated%20AI%20predictive%20analytics%20visualization%20with%20neural%20network%20diagrams%20and%20forecasting%20charts%2C%20futuristic%20data%20science%20interface%20showing%20machine%20learning%20models%20and%20trend%20predictions%2C%20professional%20teal%20and%20blue%20color%20scheme%2C%20clean%20modern%20dashboard%20design%20with%20graphs%20and%20metrics&width=800&height=600&seq=solution-002&orientation=landscape',
                reverse: true
              },
              {
                title: 'Real-Time Decision Support System',
                tag: 'Operations',
                description: 'Make informed decisions instantly with our real-time decision support platform. Aggregate data from multiple sources, apply intelligent algorithms, and receive actionable recommendations when you need them most.',
                features: [
                  'Multi-source data integration',
                  'Intelligent recommendation engine',
                  'Mobile-responsive interface',
                  'Collaborative decision-making tools'
                ],
                image: 'https://readdy.ai/api/search-image?query=real-time%20decision%20support%20system%20interface%20on%20mobile%20and%20desktop%20devices%20showing%20live%20data%20feeds%20and%20recommendation%20panels%2C%20responsive%20modern%20UI%20design%20with%20teal%20accents%2C%20collaborative%20workspace%20visualization%2C%20professional%20government%20application%20aesthetic%20with%20clean%20layouts&width=800&height=600&seq=solution-003&orientation=landscape',
                reverse: false
              }
            ].map((solution, index) => (
              <div
                key={index}
                className={`flex flex-col ${solution.reverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12`}
              >
                <div className="flex-1">
                  <div className="relative rounded-3xl overflow-hidden border-4 border-slate-200 shadow-2xl transform hover:scale-105 transition-transform duration-500">
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="flex-1 space-y-6">
                  <div className="inline-block px-4 py-2 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full">
                    {solution.tag}
                  </div>
                  
                  <h3 className="text-4xl font-bold text-slate-900">
                    {solution.title}
                  </h3>
                  
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {solution.description}
                  </p>
                  
                  <ul className="space-y-3">
                    {solution.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                        <span className="text-base text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button className="px-6 py-3 border-2 border-teal-600 text-teal-600 font-semibold rounded-full hover:bg-teal-600 hover:text-white transition-all cursor-pointer whitespace-nowrap">
                    View Case Study
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-slate-900 mb-4">Case Studies</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Real results from government organizations that transformed their operations with our solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {[
              {
                client: 'Department of Transportation',
                title: 'Reducing Traffic Congestion by 35%',
                description: 'Implemented predictive analytics to optimize traffic flow and reduce commute times across major metropolitan areas',
                image: 'https://readdy.ai/api/search-image?query=modern%20city%20traffic%20management%20system%20with%20digital%20overlay%20showing%20optimized%20traffic%20flow%20patterns%2C%20aerial%20view%20of%20urban%20transportation%20network%20with%20data%20visualization%20elements%2C%20professional%20government%20infrastructure%20technology%2C%20teal%20and%20blue%20accent%20colors%2C%20clean%20contemporary%20design&width=700&height=500&seq=case-001&orientation=landscape',
                logo: 'ri-roadster-line'
              },
              {
                client: 'Public Health Agency',
                title: 'Improving Response Time by 50%',
                description: 'Deployed AI-powered decision support system for emergency response coordination and resource allocation',
                image: 'https://readdy.ai/api/search-image?query=emergency%20response%20coordination%20center%20with%20digital%20command%20dashboard%20showing%20real-time%20health%20data%20and%20resource%20allocation%20maps%2C%20modern%20public%20health%20operations%20room%20with%20multiple%20screens%2C%20professional%20government%20facility%20with%20teal%20accent%20lighting%2C%20clean%20organized%20workspace&width=700&height=500&seq=case-002&orientation=landscape',
                logo: 'ri-heart-pulse-line'
              }
            ].map((study, index) => (
              <div
                key={index}
                className="group relative rounded-3xl overflow-hidden h-96 cursor-pointer"
              >
                <img
                  src={study.image}
                  alt={study.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-slate-900/30"></div>
                
                <div className="absolute inset-0 p-8 flex flex-col justify-between">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className={`${study.logo} text-4xl text-white`}></i>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-3xl font-bold text-white">
                      {study.title}
                    </h3>
                    <p className="text-lg text-white/90">
                      {study.description}
                    </p>
                    <div className="flex items-center justify-end">
                      <div className="w-14 h-14 rounded-full border-2 border-white flex items-center justify-center group-hover:bg-white transition-colors">
                        <i className="ri-arrow-right-line text-2xl text-white group-hover:text-slate-900"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="relative rounded-3xl overflow-hidden h-80 bg-slate-900">
            <div className="absolute inset-0 flex">
              <div className="w-2/5 bg-slate-900 p-12 flex flex-col justify-center space-y-6">
                <h3 className="text-3xl font-bold text-white">
                  Environmental Protection Initiative
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-5xl font-bold text-teal-400">40%</div>
                    <div className="text-base text-slate-300">Reduction in emissions monitoring costs</div>
                  </div>
                  <div>
                    <div className="text-5xl font-bold text-teal-400">24/7</div>
                    <div className="text-base text-slate-300">Real-time environmental tracking</div>
                  </div>
                </div>
                <button className="self-start px-6 py-3 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap">
                  Read Full Case
                </button>
              </div>
              
              <div className="w-3/5 relative">
                <img
                  src="https://readdy.ai/api/search-image?query=environmental%20monitoring%20station%20with%20digital%20sensors%20and%20data%20collection%20equipment%20in%20natural%20setting%2C%20modern%20eco-technology%20installation%20with%20real-time%20air%20quality%20monitoring%20displays%2C%20professional%20government%20environmental%20protection%20technology%2C%20clean%20green%20and%20teal%20color%20palette%2C%20contemporary%20sustainable%20design&width=900&height=500&seq=case-003&orientation=landscape"
                  alt="Environmental Protection"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-5xl font-bold text-slate-900">About MetamorphoBiz</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                We are a specialized technology partner dedicated to transforming how government organizations harness the power of data. With deep expertise in advanced analytics, artificial intelligence, and operational optimization, we deliver solutions that drive meaningful change.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Our team combines technical excellence with an understanding of government operations, ensuring our solutions meet the unique requirements of public sector organizations while maintaining the highest standards of security and compliance.
              </p>
              
              <div className="grid grid-cols-3 gap-8 pt-8">
                {[
                  { number: '50+', label: 'Government Clients' },
                  { number: '15+', label: 'Years Experience' },
                  { number: '200+', label: 'Projects Delivered' }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-4xl font-bold text-teal-600 mb-2">{stat.number}</div>
                    <div className="text-sm text-slate-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20diverse%20team%20of%20data%20scientists%20and%20analysts%20collaborating%20in%20modern%20government%20technology%20office%2C%20contemporary%20workspace%20with%20large%20screens%20showing%20data%20visualizations%2C%20corporate%20business%20environment%20with%20teal%20accent%20colors%2C%20collaborative%20meeting%20atmosphere%2C%20clean%20professional%20photography&width=800&height=900&seq=about-001&orientation=portrait"
                  alt="MetamorphoBiz Team"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-700"></div>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
            backgroundSize: '4px 4px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="flex-1 space-y-4">
              <h2 className="text-5xl font-bold text-white leading-tight">
                Ready to Transform<br />Your Data?
              </h2>
              <p className="text-xl text-white/90">
                Join the many organizations leveraging our solutions for intelligent decision-making
              </p>
            </div>

            <div className="flex-shrink-0">
              <button 
                onClick={() => scrollToSection('contact')}
                className="group flex items-center gap-4 px-8 py-5 bg-white rounded-full hover:scale-105 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
              >
                <span className="text-xl font-bold text-slate-900">Schedule a Demo</span>
                <div className="w-14 h-14 rounded-full bg-slate-900 flex items-center justify-center group-hover:bg-slate-800 transition-colors">
                  <i className="ri-arrow-right-line text-2xl text-white"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-slate-900 mb-4">Get In Touch</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Let's discuss how we can help transform your organization's data into actionable intelligence
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Address</h3>
                  <p className="text-base text-slate-600">
                    340 N Sam Houston Pkwy E Ste A165G<br />
                    Houston, TX 77060 USA
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Phone</h3>
                  <p className="text-base text-slate-600">1-844-667-7433</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Email</h3>
                  <p className="text-base text-slate-600">contact@metamorphobiz.com</p>
                </div>
              </div>

              <div className="pt-8">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  {['ri-linkedin-fill', 'ri-twitter-fill', 'ri-github-fill', 'ri-youtube-fill'].map((icon, index) => (
                    <button
                      key={index}
                      className="w-12 h-12 rounded-full border-2 border-slate-300 flex items-center justify-center hover:border-teal-600 hover:bg-teal-600 hover:text-white transition-all cursor-pointer"
                    >
                      <i className={`${icon} text-xl`}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-3xl p-8">
              <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    name="organization"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm"
                    placeholder="Your Organization"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all resize-none text-sm"
                    placeholder="Tell us about your needs..."
                  ></textarea>
                  <p className="text-xs text-slate-500 mt-1">Maximum 500 characters</p>
                </div>

                {formStatus === 'success' && (
                  <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg text-teal-800 text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-6 py-4 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-all hover:shadow-lg cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white">
        <div className="border-t border-white/10"></div>
        
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="space-y-6">
              <img 
                src="https://static.readdy.ai/image/8cd6047605fa8bd9b83f93f5c3fc7ad9/47c456da8a70802ef8f75066d63b6e27.png" 
                alt="MetamorphoBiz Logo" 
                className="h-14 w-auto brightness-0 invert"
              />
              <p className="text-base text-slate-400 leading-relaxed">
                Transforming complex data into intelligent decision systems through advanced analytics, AI and operational optimization.
              </p>
              <div className="flex gap-4">
                {['ri-linkedin-fill', 'ri-twitter-fill', 'ri-github-fill', 'ri-youtube-fill'].map((icon, index) => (
                  <button
                    key={index}
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-teal-400 hover:text-teal-400 transition-all cursor-pointer"
                  >
                    <i className={`${icon} text-lg`}></i>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">Services</h3>
              <ul className="space-y-4">
                {[
                  'AI-Driven Operational Intelligence',
                  'Predictive Analytics & Forecasting',
                  'Fraud Detection & Risk Modeling',
                  'Data Strategy & Architecture',
                  'Business Intelligence Dashboards',
                  'Workflow Automation'
                ].map((item, index) => (
                  <li key={index}>
                    <button className="text-base text-slate-400 hover:text-white hover:translate-x-1 transition-all cursor-pointer whitespace-nowrap">
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">Solutions</h3>
              <ul className="space-y-4">
                {['Data Governance', 'Predictive Analytics', 'Decision Support', 'Cloud Integration'].map((item, index) => (
                  <li key={index}>
                    <button className="text-base text-slate-400 hover:text-white hover:translate-x-1 transition-all cursor-pointer whitespace-nowrap">
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">Contact</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-base text-teal-400 mt-1"></i>
                  <span className="text-base text-slate-400">
                    340 N Sam Houston Pkwy E Ste A165G<br />Houston, TX 77060 USA
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-phone-line text-base text-teal-400"></i>
                  <span className="text-base text-slate-400">+1-844-667-7433</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-mail-line text-base text-teal-400"></i>
                  <span className="text-base text-slate-400">contact@metamorphobiz.com</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-slate-400">
                © 2024 MetamorphoBiz. All rights reserved.
              </p>
              <div className="flex gap-8">
                {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((item, index) => (
                  item === 'Privacy Policy' ? (
                    <Link key={index} to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                      {item}
                    </Link>
                  ) : (
                    <button key={index} className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                      {item}
                    </button>
                  )
                ))}
                <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}